// File: src/pages/Wallet.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl } from "../../Environments/environment";
import { toast } from "react-toastify";
import { Switch } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import Transactions from "./Transaction";
import Edit from "../../Assets/edit.svg";
import {
  formatPaymentAmount,
  isIndiaProperty,
  getPropertyCountry,
} from "./paymentCurrency.util";
/**
 * DESIGN REFERENCE (image file):
 * /mnt/data/60c16f84-f0dd-48a7-b827-dc393b48552d.png
 *
 * Wallet page shows current credit, auto-topup settings and Add Credits button
 */

export default function Wallet() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [list, setList] = useState([]);
  const { selectedPropertyId } = useProperty();
  const propertyId = selectedPropertyId;
  const propertyCountry = getPropertyCountry(propertyId);
  const isIndia = isIndiaProperty(propertyCountry);

  const formatAmount = (cents, currency) =>
    formatPaymentAmount(cents, currency ?? propertyCountry);
  const formatDate = (ts) => new Date(ts).toLocaleString();

  const badge = (status) => {
    switch (status) {
      case "succeeded":
        return (
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded bg-green-100 text-green-700 whitespace-nowrap">
            Succeeded
          </span>
        );
      case "failed":
        return (
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded bg-red-100 text-red-700 whitespace-nowrap">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded bg-yellow-100 text-yellow-700 whitespace-nowrap">
            Pending
          </span>
        );
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/stripe/wallet/${propertyId}`,
      );
      setWallet(response.data.result);
    } catch (err) {
      console.error("Failed to load wallet", err);
    } finally {
      setLoading(false);
    }
  };
  const loadRecentTrans = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/stripe/transactions/${propertyId}?recent=${true}`,
      );
      const data = response.data.result;
      setList(data || []);
    } catch (err) {
      console.error("Failed to load transactions:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    loadRecentTrans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBillingPortal = async () => {
    try {
      const response = await apiClient.post(
        `${baseUrl}/stripe/billing-portal-session`,
        {
          customerId: wallet.stripeCustomerId,
          propertyId,
        },
      );
      window.open(response.data.result.url, "_blank");
    } catch (err) {
      console.error(err);
      toast.error("Failed to open billing portal");
    }
  };
  const handleToggleAutoTopUp = async (value) => {
    if (isIndia) {
      toast.error("Auto top-up is not available for India properties.");
      return;
    }

    const updatedWallet = {
      ...wallet,
      isAutoTopUpEnabled: value,
    };

    setWallet(updatedWallet);

    if (isEditOpen) return;

    try {
      setLoadingSave(true);
      const response = await apiClient.put(
        `${baseUrl}/stripe/wallet`,
        updatedWallet,
      );

      if (response.data.statusCode === 200) {
        toast.success("Updated Wallet setting successfully!");
        setIsEditOpen(false);
        load();
      }
    } catch (error) {
      console.error("Error while Updating Wallet setting :", error);

      toast.error(
        error.response?.data?.message || "Failed to update Wallet setting",
      );
    } finally {
      setLoadingSave(false);
    }
  };
  const handleUpdateWallet = async () => {
    try {
      setLoadingSave(true);
      if (wallet.topUpCredit < 500) {
        toast.error("Minimum Top Up is 500 reviews.");
        return;
      } else if (wallet.lowThreshold < 100) {
        toast.error("Minimum Low Threshold is 100 reviews.");
        return;
      }
      const response = await apiClient.put(`${baseUrl}/stripe/wallet`, wallet);

      if (response.data.statusCode === 200) {
        toast.success("Updated Wallet setting successfully!");
        setIsEditOpen(false);
        load();
      }
    } catch (error) {
      console.error("Error while Updating Wallet setting :", error);
      toast.error(
        error.response?.data?.message || "Failed to update Wallet setting",
      );
    } finally {
      setLoadingSave(false);
    }
  };

  const low = wallet?.currentCredit < wallet?.lowThreshold;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex flex-row flex-wrap items-center justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold whitespace-nowrap">
          Manage Payments
        </h1>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {loading ? (
            <>
              <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-8 w-24 sm:w-28 bg-gray-200 rounded animate-pulse"></div>
            </>
          ) : (
            <>
              <button
                onClick={openBillingPortal}
                className="px-3 sm:px-4 py-1.5 sm:py-2 border rounded-md text-xs sm:text-sm whitespace-nowrap flex-shrink-0 hover:bg-gray-50"
              >
                Manage Payment Methods
              </button>
              <button
                onClick={() => navigate("/dashboard/add-credits")}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white rounded-md text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
              >
                Buy Reviews
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className=" flex  md:flex-row flex-col border rounded-md bg-white shadow-sm">
          {/* Skeleton for Current Balance Card */}
          <div className="p-3 sm:p-4 ">
            <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse"></div>
            <div className="h-8 sm:h-10 w-32 bg-gray-200 rounded mb-4 animate-pulse"></div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-6 w-11 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          </div>

          {/* Skeleton for Threshold/Top-up Card */}
          <div className="p-3 sm:p-4 grow">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex-1">
                <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex  md:flex-row flex-col border rounded-md bg-white shadow-sm">
          {/* CURRENT BALANCE */}
          <div className="p-3 sm:p-4 ">
            <div className="text-xs sm:text-sm text-gray-500">
              Current Reviews
            </div>
            <div className="text-2xl sm:text-3xl font-bold mt-1">
              {wallet?.credits ?? 0} Reviews
            </div>

            {/* AUTO TOP-UP WITH TOOLTIP */}
          </div>

          {/* THRESHOLD + TOP UP AMOUNT */}
          {!isIndia && (   <div className="p-3 sm:p-4 grow">
            <div className="text-xs sm:text-sm text-gray-500 mt-3 flex items-center justify-start sm:justify-end gap-2 group relative flex-wrap">
              <label className="text-gray-800 font-semibold">
                Auto Top-up:
              </label>

              {/* Tooltip Icon */}
              <InformationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-indigo-600 cursor-pointer flex-shrink-0" />

              {/* Tooltip box */}
              <div
                className="absolute right-0 top-6 z-10
        hidden group-hover:block
        bg-black text-white text-xs px-2 py-1 rounded shadow-md w-max max-w-[200px] sm:max-w-none"
              >
                Enable this to automatically buy reviews when balance drops
                below threshold.
              </div>

              <Switch
                checked={
                  isIndia ? false : (wallet?.isAutoTopUpEnabled ?? false)
                }
                onChange={handleToggleAutoTopUp}
                disabled={loadingSave || isIndia}
                className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer
        rounded-full border-2 border-transparent bg-gray-300 transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
        data-[checked]:bg-indigo-600"
              >
                <span className="sr-only">Enable auto top-up</span>
                <span
                  aria-hidden="true"
                  className="pointer-events-none inline-block h-5 w-5 transform rounded-full 
          bg-white shadow ring-0 transition duration-200 ease-in-out 
          group-data-[checked]:translate-x-5"
                />
              </Switch>
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-x-4">
              {/* LOW THRESHOLD FIELD WITH TOOLTIP */}
              <div className="relative flex-1">
                <div className="flex items-center justify-end gap-1 group mt-2">
                  <span className="text-xs sm:text-sm text-gray-500">
                    <span className="text-gray-800 font-semibold ">
                      {" "}
                      {!isEditOpen && `${wallet?.topUpCredit} `}
                    </span>
                    {isEditOpen && (
                      <input
                        type="number"
                        value={wallet.topUpCredit}
                        min={1}
                        onChange={(e) =>
                          setWallet({
                            ...wallet,
                            topUpCredit: e.target.value,
                          })
                        }
                        className="w-[80px] p-1 border rounded-md mt-1 text-sm sm:text-base
focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      />
                    )}{" "}
                    reviews auto top-up will be triggered when the balance is
                    below
                    <span className="text-gray-800 font-semibold ml-2">
                      {!isEditOpen && `${wallet?.lowThreshold}`}
                    </span>
                    {isEditOpen && (
                      <input
                        type="number"
                        value={wallet.lowThreshold}
                        min={1}
                        onChange={(e) =>
                          setWallet({
                            ...wallet,
                            lowThreshold: e.target.value,
                          })
                        }
                        className="ml-2 w-[80px] p-1 border rounded-md mt-1 text-sm sm:text-base
focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      />
                    )}
                    {!isEditOpen && (
                      <button
                        className="text-indigo-600 hover:text-indigo-900 text-xl flex-shrink-0  ml-2"
                        onClick={(e) => setIsEditOpen(true)}
                        aria-label="Edit wallet settings"
                      >
                        <img
                          src={Edit}
                          alt="Edit"
                          className="w-4 h-4 sm:w-5 sm:h-5 mb-[-2px]"
                        />
                      </button>
                    )}
                    {isEditOpen && (
                      <>
                        {" "}
                        <button
                          onClick={() => {
                            setIsEditOpen(false);
                            load();
                          }}
                          className="p-1.5 border rounded-md hover:bg-gray-100 text-sm sm:text-base  sm:w-auto ml-3"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateWallet}
                          disabled={loadingSave}
                          className="p-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-sm sm:text-base sm:w-auto ml-2"
                        >
                          {loadingSave ? "Saving..." : "Save"}
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>)}
       
        </div>
      )}

      {loading ? (
        <>
          {/* Skeleton for Warning */}
          <div className="mb-4 p-3 sm:p-4 bg-gray-50 border-l-4 border-gray-200 rounded animate-pulse">
            <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 w-full bg-gray-200 rounded"></div>
          </div>

          {/* Skeleton for Buttons */}
          <div className="mb-4 sm:mb-6 flex flex-row items-center gap-2 sm:gap-3">
            <div className="h-9 w-40 sm:w-44 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-9 w-32 sm:w-36 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Skeleton for Table */}
          <div className="mt-6 sm:mt-8">
            <div className="h-6 w-32 bg-gray-200 rounded mb-3 animate-pulse"></div>
            <div className="border rounded-md bg-white overflow-hidden">
              <div className="p-3 sm:p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {low && (
            <div className="mb-4 p-3 sm:p-4 bg-yellow-50 border-l-4 border-yellow-300 rounded">
              <div className="font-medium text-sm sm:text-base text-yellow-800">
                Low reviews balance
              </div>
              <div className="text-xs sm:text-sm text-yellow-700 mt-1">
                Your balance is below the low threshold. Add reviews now or
                enable auto-top-up.
              </div>
            </div>
          )}

          <Transactions />
        </>
      )}
    </div>
  );
}
